import { useRef } from 'react';

import { IconCategoryPlus, IconPlus, IconUserPlus } from '@tabler/icons-react';
import { useClickOutside } from 'hooks/useClickOutside';

import { Typography } from '@cfa/react-components';
import { BaseButton, Popover, Surface, Tooltip } from '@cfa/react-core';

import { PlanPlus, QuizPlus } from '@/icons';

interface FabProps {
  children?: React.ReactNode;
  isOpen?: boolean;
  onClick: () => void;
  onClose?: () => void;
  tooltip?: string;
  /**
   * The icon to display on the Fab.
   * @default 'plus'
   */
  icon?: 'plus' | 'plan-plus' | 'quiz-plus' | 'add-team-member' | 'add-content';
  className?: string;
  'data-testid'?: string;
}

export const Fab: React.FC<FabProps> = ({
  onClick,
  onClose = () => {},
  isOpen,
  children,
  tooltip,
  className,
  icon = 'plus',
  'data-testid': dataTestId,
}) => {
  const ref = useRef(null);
  useClickOutside(ref, () => {
    onClose();
    onClick();
  });

  const iconButton = (
    <Surface
      className="bg-brand-navy! fixed right-8 bottom-[calc(--spacing(8)+var(--native-bottom-inset))] z-4 rounded-full!"
      elevation={2}
    >
      <BaseButton
        aria-label="Add Button"
        data-testid={dataTestId ?? 'AddFAB'}
        className="flex items-center justify-center"
        onPress={onClick}
      >
        <span className="flex size-14 items-center justify-center p-3">
          {icon === 'plus' && <IconPlus height={24} width={24} color="white" />}
          {icon === 'plan-plus' && (
            <PlanPlus height={24} width={24} color="white" />
          )}
          {icon === 'quiz-plus' && (
            <QuizPlus height={24} width={24} color="white" />
          )}
          {icon === 'add-team-member' && (
            <IconUserPlus height={24} width={24} color="white" />
          )}
          {icon === 'add-content' && (
            <IconCategoryPlus height={24} width={24} color="white" />
          )}
        </span>
      </BaseButton>
    </Surface>
  );

  const content = tooltip ? (
    <Tooltip.Root delay={250}>
      {iconButton}
      <Tooltip.Content placement="left">
        <Typography fontWeight="bold" variant="body2">
          {tooltip}
        </Typography>
      </Tooltip.Content>
    </Tooltip.Root>
  ) : (
    <Popover.Root isOpen={isOpen}>
      {iconButton}
      <Popover.Panel className="p-0!" placement="top end">
        <div ref={ref}>{children}</div>
      </Popover.Panel>
    </Popover.Root>
  );
  return <div className={className}>{content}</div>;
};

export default Fab;
